import { useEffect, useState } from "react";
import { fetchAnchors } from "../api/client";
import { DEFAULT_ANCHOR } from "../hooks/useSharedParams";
import { useSharedParams } from "../hooks";
import { ALL_SALES_CHANNELS } from "./FilterBar";
import { anchorFeature, isFeatureEnabled } from "../features";
import { SingleSelect } from "./SingleSelect";

/**
 * Anchor (product family) selector shown as the first top dropdown in the
 * page filter bar. Fetches available anchors from the backend and persists
 * the selection via the `anchor` URL search param.
 */
export function AnchorSelector() {
    const { anchor: current, setAnchor } = useSharedParams(ALL_SALES_CHANNELS);
    const [anchors, setAnchors] = useState<string[] | null>(null);
    const [anchorListLoaded, setAnchorListLoaded] = useState(false);
    const visibleAnchors = anchors?.filter((anchor) => {
        const feature = anchorFeature(anchor);
        return !feature || isFeatureEnabled(feature);
    });

    useEffect(() => {
        let alive = true;
        fetchAnchors()
            .then((list) => {
                if (alive) {
                    setAnchors(list);
                    setAnchorListLoaded(true);
                }
            })
            .catch(() => {
                if (alive) setAnchors([DEFAULT_ANCHOR]);
            });
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (
            anchorListLoaded &&
            visibleAnchors?.length &&
            current !== DEFAULT_ANCHOR &&
            !visibleAnchors.includes(current)
        ) {
            setAnchor(DEFAULT_ANCHOR);
        }
    }, [anchorListLoaded, visibleAnchors, current, setAnchor]);

    if (!visibleAnchors) return null;

    return (
        <SingleSelect
            label="Product family"
            tooltip="Product family to analyse"
            options={visibleAnchors.map((a) => ({ value: a, label: a }))}
            value={current}
            onChange={setAnchor}
        />
    );
}
